import { formatPhoneNumberIntl, isValidPhoneNumber } from "react-phone-number-input"
import { stripHtml } from "string-strip-html"
import validator from "validator"
// import usMap from "./usMap.json"
// import worldMap from "./worldMap.json"
import pluralize from "pluralize"
import { FormFieldTracking, Option } from "./types"
import { ExtendedLanguageList } from "ts-extended-language-list/dist/language-list"
import { trackAnalyticsEvent } from "@web/context/analytics/client"
import { formatPhoneNumberDisplay } from "platform-common/util"

export const INTERNATIONAL_PHONE_PREFIX = "+"

export const YES = "YES"
export const NO = "NO"

export const YES_NO_OPTIONS = [
  { value: YES, label: "Yes" },
  { value: NO, label: "No" },
]

export const LANGUAGE_OPTIONS = ExtendedLanguageList.getLanguageNames()
  // Have English and Spanish on top
  .sort((a, b) =>
    a === "English" ? -1 : b === "English" ? 1 : a === "Spanish" ? -1 : b === "Spanish" ? 1 : a.localeCompare(b)
  )
  .map((language) => ({
    label: language,
    value: language,
  }))

export const OTHER_OPTION = { value: "OTHER", label: "Other" }

export type YesNo = typeof YES | typeof NO

// export const GEOGRAPHIES = {
//   worldMap: {
//     dataSource: worldMap,
//     width: 800,
//     height: 520,
//     projectionConfig: { center: [0, 40], scale: 120 } as ProjectionConfig,
//   },
//   usMap: {
//     dataSource: usMap,
//     width: 1000,
//     height: 600,
//     projectionConfig: { center: [-96, 36], scale: 900 } as ProjectionConfig,
//   },
// }

// export type GeographyType = (typeof GEOGRAPHIES)[keyof typeof GEOGRAPHIES]

/**
 * Format an inputted phone number.
 * Assume it's a US/CA number if it's a ten digit number or starts with 1.
 */
export const getFormattedPhoneNumber = (value: string) => {
  value = value.trim()
  const cleanValue = value.replace(/[^\d]/g, "") // get only numbers

  // Prepend "+" if its missing in a non-empty string
  const valueWithInternationalPrefix = [INTERNATIONAL_PHONE_PREFIX, undefined].includes(cleanValue[0])
    ? cleanValue
    : INTERNATIONAL_PHONE_PREFIX.concat(cleanValue)

  // prefer US/CA format if possible
  if (
    // probably a US/CA number:
    cleanValue.length <= 10 ||
    (cleanValue.length === 11 && cleanValue.startsWith("1")) ||
    (cleanValue.length === 10 && value.length === 10 && !value.startsWith("0") && !value.startsWith("+"))
  )
    return formatPhoneNumberDisplay(value)

  // formatPhoneNumberIntl returns empty string if formatting fails, so we return raw value in such case
  return stripePhoneNumber(formatPhoneNumberIntl(valueWithInternationalPrefix as any) || valueWithInternationalPrefix) // should be tagged as E164Number
}

const stripePhoneNumber = (value: string) => value.replace(/[^0-9+() -]/g, "")

export const getFormattedMoney = (value: string) => {
  const cleanValue = value.replace(/[^\d]/g, "") // get only numbers

  if (value.trim() === "$") return value
  if (!parseInt(cleanValue)) return ""

  const formattedValue = `$${(parseInt(cleanValue) || 0).toLocaleString()}`

  return formattedValue + `${value.match(/\s+$/)?.[0] || ""}`
}

export const getFormattedMoneyRange = (value: string) => {
  if (!value.includes("-")) return getFormattedMoney(value)

  const [min, max] = value.split("-")
  if (!min || !max) return getFormattedMoney(value)

  const cleanMax = max.replace(/[^\d]/g, "")

  if (!parseInt(cleanMax)) return value.replace(/[^\d\s-$,]/g, "").replace(/-{2,}/, "-")

  const formattedValue = `${getFormattedMoney(min.trim())} - ${getFormattedMoney(max.trim())}`

  return formattedValue
}

export const cleanUpMoneyRangeValue = (value: string) => {
  if (value.includes("-")) {
    const [min, max] = value.split("-")
    if (!min || !max) return value
    const cleanMax = max.replace(/[^\d]/g, "")

    if (!parseInt(cleanMax)) return min.trim()
  }

  if (value.trim() === "$") return ""

  return value
}

export const validateEmail = (input: string) => validator.isEmail(input) || "Please provide a valid email address"

export const validateUrl = (input: string) => validator.isURL(input) || "Please provide a valid URL"

export const validatePhone = (input: string) => isValidPhoneNumber(input) || "Please provide a valid phone number"

export const requireTextInRichTextField = (errorMessage: string) => (value: string) =>
  !!stripHtml(value).result || errorMessage

export const required = (fieldName: string, message?: string) => ({
  value: true,
  message: message || `${fieldName} is required`,
})

export const minAmount = (length: number, errorMessage?: string) => (value?: unknown[]) =>
  (value?.length || 0) >= length || errorMessage || `Please select at least ${length}`

export const minLength =
  (length: number, type: "raw" | "html", fieldName?: string, errorMessage?: string) => (value: string) =>
    (type === "html" ? stripHtml(value || "").result : value).length >= length ||
    errorMessage ||
    `${fieldName || "Length"} should be at least ${length} ${pluralize("character", length)}`

export const maxLength = (length: number, type: "raw" | "html", fieldName?: string) => (value: string) =>
  (type === "html" ? stripHtml(value).result : value).length <= length ||
  `${fieldName || "Length"} should be no more than ${length} ${pluralize("character", length)}`

export const maxWords = (max: number, errorMessage?: string) => (value?: string) =>
  (value?.split(/\s+/).length || 0) <= max || errorMessage || `Please type no more than ${max} words`

export const preventSubmitOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key === "Enter") {
    e.preventDefault()
  }
}

export const nameAndIdToOption = ({ name, id }: { name: string; id: string }) => ({
  value: id,
  label: name,
})

export const normalizeLabel = (option: Option) => option.label.trim().toLowerCase()

export const isValueEmpty = (value: any) => {
  if (Array.isArray(value)) return !value.length

  if (typeof value === "string") return !stripHtml(value).result.trim()

  return value == undefined
}

export const isHtmlContentEmpty = (html: string) => stripHtml(html).result.trim().length == 0

export const trackFormEvent = async (
  event: "Form Value Provided" | "Form Field Completed" | "Form Field Skipped",
  tracking: FormFieldTracking, // individual field tracking data
  fieldName: string, // camelCase value in form e.g. "jobTitle"
  label?: string // human readable label e.g. "What is the job title?"
) => {
  await trackAnalyticsEvent({
    event,
    fieldName: tracking.fieldName ?? label ?? fieldName,
    vacancyId: tracking.vacancy?.id,
    vacancyName: tracking.vacancy?.name,
  })
}
